import { useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { createPortfolio, resolvePortfolioCurrency, addFundsToPortfolioBulk } from './addToPortfolioService';

interface UseBulkAddToPortfolioParams {
  funds: any[] | undefined;
  isCreatingNew: boolean;
  newPortfolioName: string;
  portfolioType: string | undefined;
  selectedPortfolioId: string | undefined | null;
  portfolios: Array<{ id: string; portfolio_type?: string }> | undefined;
  userId?: string;
  setIsProcessing: (b: boolean) => void;
  setBulkProgress: (v: { current: number; total: number }) => void;
  setBulkResults: (v: { success: number; failed: number; errors: string[] }) => void;
  onSuccess?: () => void;
  handleClose: () => void;
}

export function useBulkAddToPortfolio({
  funds,
  isCreatingNew,
  newPortfolioName,
  portfolioType,
  selectedPortfolioId,
  portfolios,
  userId,
  setIsProcessing,
  setBulkProgress,
  setBulkResults,
  onSuccess,
  handleClose,
}: UseBulkAddToPortfolioParams) {
  return useCallback(async () => {
    if (!funds || (funds?.length || 0) === 0) return;

    if (!selectedPortfolioId && !isCreatingNew) {
      toast.error('Please select a portfolio or create a new one');
      return;
    }
    if (isCreatingNew && !newPortfolioName.trim()) {
      toast.error('Please enter a portfolio name');
      return;
    }

    setIsProcessing(true);

    try {
      let targetPortfolioId = selectedPortfolioId as string | undefined;
      let targetPortfolioType: string | undefined;

      if (isCreatingNew) {
        if (!userId) throw new Error('User not authenticated');
        const newPortfolio = await createPortfolio(userId, newPortfolioName, portfolioType || 'model');
        targetPortfolioId = newPortfolio.id;
        targetPortfolioType = portfolioType || 'model';
        toast.success(`Portfolio "${newPortfolioName}" created successfully`);
      }

      if (!isCreatingNew) {
        const selected = (portfolios || []).find(p => p.id === targetPortfolioId);
        targetPortfolioType = selected?.portfolio_type || 'personal';
      }

      const currencyCode = await resolvePortfolioCurrency(targetPortfolioId as string);

      setBulkProgress({ current: 0, total: funds?.length || 0 });
      setBulkResults({ success: 0, failed: 0, errors: [] });

      const results = await addFundsToPortfolioBulk({
        funds,
        targetPortfolioId: targetPortfolioId as string,
        targetPortfolioType,
        currencyCode,
        onProgress: (info) => {
          setBulkProgress({ current: info.current, total: info.total });
          setBulkResults({ success: info.success, failed: info.failed, errors: info.errors });
        }
      });

      if (results.successCount > 0 && results.failedCount === 0) {
        toast.success(`Successfully added all ${results.successCount} item(s) to portfolio`);
      } else if (results.successCount > 0 && results.failedCount > 0) {
        toast.success(`Added ${results.successCount} item(s) to portfolio. ${results.failedCount} failed (see details below)`);
        if ((results.errors?.length || 0) > 0) {
          // eslint-disable-next-line no-console
          console.group('Failed items:');
          results.errors.forEach((e) => console.warn(e));
          console.groupEnd();
        }
      } else if (results.failedCount > 0) {
        toast.error(`Failed to add ${results.failedCount} item(s) to portfolio`);
        if ((results.errors?.length || 0) > 0) {
          // eslint-disable-next-line no-console
          console.group('Failed items:');
          results.errors.forEach((e) => console.warn(e));
          console.groupEnd();
        }
      }

      if (onSuccess && results.successCount > 0) onSuccess();
      handleClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in bulk add:', error);
      toast.error('Failed to add funds to portfolio');
    } finally {
      setIsProcessing(false);
    }
  }, [
    funds,
    isCreatingNew,
    newPortfolioName,
    portfolioType,
    selectedPortfolioId,
    portfolios,
    userId,
    setIsProcessing,
    setBulkProgress,
    setBulkResults,
    onSuccess,
    handleClose,
  ]);
}
